import type { JSX } from "react";
import { t, type Lang } from "@/lib/i18n";

export type Page = "dashboard" | "pools" | "wallet" | "referrals" | "profile";

interface NavigationProps {
  activePage: Page;
  onNavigate: (page: Page) => void;
  lang: Lang;
}

const navItems: { page: Page; labelKey: Parameters<typeof t>[0]; icon: (active: boolean) => JSX.Element }[] = [
  {
    page: "dashboard",
    labelKey: "nav.dashboard",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    page: "pools",
    labelKey: "nav.pools",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    page: "wallet",
    labelKey: "nav.wallet",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
        <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
        <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
      </svg>
    ),
  },
{
    page: "profile",
    labelKey: "nav.profile",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="5" />
        <path d="M20 21a8 8 0 0 0-16 0" />
      </svg>
    ),
  },
];

export function Navigation({ activePage, onNavigate, lang }: NavigationProps) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50" style={{ background: "rgba(0, 0, 0, 0.88)", backdropFilter: "blur(20px) saturate(1.1)", WebkitBackdropFilter: "blur(20px) saturate(1.1)", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="max-w-lg mx-auto flex justify-around items-center py-2 px-2">
        {navItems.map((item) => {
          const isActive = activePage === item.page;
          return (
            <button
              key={item.page}
              onClick={() => onNavigate(item.page)}
              className={`flex flex-col items-center gap-1 py-1.5 px-4 rounded-xl transition-all duration-300 relative ${
                isActive
                  ? "text-primary"
                  : "text-muted-foreground/60 hover:text-muted-foreground"
              }`}
              aria-label={t(item.labelKey, lang)}
              aria-current={isActive ? "page" : undefined}
            >
              {isActive && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full bg-primary" />
              )}
              {item.icon(isActive)}
              <span className={`text-[9px] tracking-wider ${isActive ? "font-bold" : "font-medium"}`}>
                {t(item.labelKey, lang)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
